import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
 // CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  //Edit as EditIcon,
  //Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import { usersApi, Reseller } from '@/api/users-api';

export default function ResellersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactDetails: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchResellers = async () => {
    setLoading(true);
    try {
      const data = await usersApi.getResellers();
      setResellers(data);
    } catch (error) {
      console.error('Error fetching resellers:', error);
      setError('Failed to load resellers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResellers();
  }, []);

  const handleCreateReseller = async () => {
    try {
      await usersApi.createReseller({
        name: formData.name,
        contactDetails: formData.contactDetails,
        distributorUserId: user?.id || '',
      });
      setOpenDialog(false);
      resetForm();
      setSuccessMessage('Reseller created successfully');
      await fetchResellers();
    } catch (error) {
      console.error('Error creating reseller:', error);
      setError('Failed to create reseller. Please try again.');
    }
  };

  const handleOpenDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contactDetails: '',
    });
  };

  const columns: Column<Reseller>[] = [
    {
      id: 'name',
      label: 'Reseller Name',
      minWidth: 150,
    },
    {
      id: 'contactDetails',
      label: 'Contact Information',
      minWidth: 200,
    },
    {
      id: 'createdAt',
      label: 'Created On',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  return (
    <>
      <PageHeader
        title="Resellers"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Resellers' },
        ]}
        action={{
          label: 'Add Reseller',
          onClick: handleOpenDialog,
          icon: <AddIcon />,
        }}
      />

      <DataTable
        columns={columns}
        data={resellers}
        loading={loading}
        getRowId={(row) => row.resellerId}
        onRowClick={(row) => navigate(`/resellers/${row.resellerId}`)}
        actions={[
          {
            icon: <ViewIcon />,
            label: 'View Details',
            onClick: (row) => navigate(`/resellers/${row.resellerId}`),
          },
        ]}
        emptyMessage="No resellers found. Add your first reseller to get started."
      />

      {/* Create Reseller Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Reseller</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Reseller Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Contact Information"
              value={formData.contactDetails}
              onChange={(e) => setFormData({ ...formData, contactDetails: e.target.value })}
              multiline
              rows={3}
              required
              helperText="Include contact details like email, phone number, and address."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReseller}
            disabled={!formData.name || !formData.contactDetails}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error and success notifications */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
